import React, { useState, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { LogOut, Menu, BarChart3, Sun, Moon, FileImage, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

// Layout components
import { AppSidebar } from '../components/layout/AppSidebar';
import { MobileDrawer } from '../components/layout/MobileDrawer';

// Analysis components
import { BatchAnalysis } from '../components/analysis/BatchAnalysis';
import { SizeFilter } from '../components/analysis/SizeFilter';

// Existing components
import DetectionResultCard from '../components/DetectionResultCard';
import ExportModal from '../components/ExportModal';
import { ExportData } from '../utils/exportUtils';
import { CLASS_COLORS, CLASS_BADGE_VARIANTS } from '../constants/detectionClasses';
import { SIDEBAR_CLASSES } from '../constants/layout';

// Hooks
import { useScrollToResult } from '../hooks/useScrollToResult';
import { useTheme } from '../contexts/ThemeContext';
import { API_BASE_URL } from '../config/api';

// Types
import { UploadedFile, InferenceResult, BatchHistogram, BatchAnalysisData, Detection } from '../types/detection';

const ALLOWED_FORMATS = ['.tif', '.tiff', '.png', '.jpg', '.jpeg', '.bmp'];
const MAX_ERROR_SNIPPET = 400;

const truncateForLog = (value: string, limit = MAX_ERROR_SNIPPET): string => {
  if (!value) {
    return '';
  }
  if (value.length <= limit) {
    return value;
  }
  return `${value.slice(0, limit)}...`;
};

const normalizeErrorDetail = (detail: unknown): string => {
  if (typeof detail === 'string') {
    return detail;
  }
  if (Array.isArray(detail)) {
    return detail.map((item) => normalizeErrorDetail(item)).join('; ');
  }
  if (detail && typeof detail === 'object') {
    return JSON.stringify(detail);
  }
  return '';
};

const parseApiResponse = async <T,>(response: Response, context: string): Promise<T> => {
  const contentType = response.headers.get('content-type') || '';
  const requestId = response.headers.get('x-request-id') || '-';
  const rawBody = await response.text();
  let bodySnippet: string | null = null;
  const getBodySnippet = (): string => {
    if (bodySnippet !== null) {
      return bodySnippet;
    }
    bodySnippet = truncateForLog(rawBody.replace(/\s+/g, ' ').trim());
    return bodySnippet;
  };

  let parsedBody: unknown = null;
  if (rawBody) {
    try {
      parsedBody = JSON.parse(rawBody);
    } catch {
      parsedBody = null;
    }
  }

  if (!response.ok) {
    const detailText = normalizeErrorDetail(
      parsedBody && typeof parsedBody === 'object' && parsedBody !== null && 'detail' in parsedBody
        ? (parsedBody as { detail?: unknown }).detail
        : null
    );
    const fallback = getBodySnippet() || `${response.status} ${response.statusText}`;

    console.error(`[API][${context}] request failed`, {
      status: response.status,
      statusText: response.statusText,
      contentType,
      requestId,
      bodySnippet: getBodySnippet(),
    });

    throw new Error(
      `${detailText || fallback} (status ${response.status}, request-id ${requestId})`
    );
  }

  if (parsedBody === null) {
    console.error(`[API][${context}] expected JSON response but received non-JSON`, {
      status: response.status,
      contentType,
      requestId,
      bodySnippet: getBodySnippet(),
    });
    throw new Error(
      `Unexpected non-JSON response from server (status ${response.status}, request-id ${requestId})`
    );
  }

  return parsedBody as T;
};

const LandingPage: React.FC = () => {
  // Mobile drawer state
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  // File management state
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Processing state
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingProgress, setProcessingProgress] = useState(0);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Settings state
  const [confThreshold, setConfThreshold] = useState(0.25);
  const [pixelToUm, setPixelToUm] = useState(1.3);

  // Results state
  const [batchHistograms, setBatchHistograms] = useState<Record<string, BatchHistogram>>({});
  const [isLoadingHistograms, setIsLoadingHistograms] = useState(false);
  const [exclusionsByFile, setExclusionsByFile] = useState<Record<string, Set<number>>>({});
  const [regeneratedHistogramsByFile, setRegeneratedHistogramsByFile] = useState<Record<string, Record<string, string>>>({});

  // Size filter state
  const [sizeFilterEnabled] = useState(true);
  const [sizeFilterRange, setSizeFilterRange] = useState<[number, number]>([0, 100]);
  const [appliedSizeFilterRange, setAppliedSizeFilterRange] = useState<[number, number]>([0, 100]);

  // Export modal state
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);

  // Active file for sidebar highlight
  const [activeFileId, setActiveFileId] = useState<string | undefined>();

  // Preview state for pending files
  const [previewFileId, setPreviewFileId] = useState<string | undefined>();
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);

  // Memoize the preview file to avoid finding it on every render
  const previewFile = React.useMemo(() => {
    if (!previewFileId) return null;
    return uploadedFiles.find(f => f.id === previewFileId && f.status === 'pending') || null;
  }, [previewFileId, uploadedFiles]);

  // Handle TIFF conversion and preview URL generation
  React.useEffect(() => {
    if (!previewFile) {
      setPreviewUrl(null);
      return;
    }

    const fileType = previewFile.file.type.toLowerCase();
    const fileName = previewFile.file.name.toLowerCase();
    const isTiff = fileType.includes('tiff') || fileType.includes('tif') ||
                   fileName.endsWith('.tif') || fileName.endsWith('.tiff');

    if (!isTiff) {
      // For non-TIFF files, use blob URL directly
      const url = URL.createObjectURL(previewFile.file);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }

    // For TIFF files, decode using tiff library
    setIsLoadingPreview(true);

    const decodeTiff = async () => {
      try {
        const arrayBuffer = await previewFile.file.arrayBuffer();
        // @ts-ignore - tiff module type resolution issue with current tsconfig
        const { decode } = await import('tiff');

        // Decode TIFF to ImageData array
        const ifds = await decode(arrayBuffer);

        if (!ifds || ifds.length === 0) {
          throw new Error('No images found in TIFF file');
        }

        // Get the first image - it's a TiffIfd object
        const tiffIfd = ifds[0];

        // Get image dimensions and pixel data from TiffIfd
        const width = tiffIfd.width;
        const height = tiffIfd.height;

        // Get the raw pixel data (16-bit grayscale)
        const rawData = tiffIfd.data; // Uint16Array

        // Create canvas
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          throw new Error('Failed to get canvas context');
        }

        // Create ImageData and convert 16-bit grayscale to 8-bit RGBA
        const imageData = ctx.createImageData(width, height);
        const pixels = imageData.data; // Uint8ClampedArray (RGBA)

        // Convert 16-bit grayscale to 8-bit RGBA
        for (let i = 0; i < rawData.length; i++) {
          // Scale 16-bit (0-65535) to 8-bit (0-255)
          const gray = Math.round((rawData[i] / 65535) * 255);
          const idx = i * 4;
          pixels[idx] = gray;     // R
          pixels[idx + 1] = gray; // G
          pixels[idx + 2] = gray; // B
          pixels[idx + 3] = 255;  // A (fully opaque)
        }

        // Draw the ImageData to canvas
        ctx.putImageData(imageData, 0, 0);

        // Convert canvas to blob URL
        canvas.toBlob((blob: Blob | null) => {
          if (blob) {
            const url = URL.createObjectURL(blob);
            setPreviewUrl(url);
            setIsLoadingPreview(false);
          } else {
            setPreviewUrl(null);
            setIsLoadingPreview(false);
          }
        }, 'image/png');
      } catch (error) {
        console.error('Error decoding TIFF:', error);
        setPreviewUrl(null);
        setIsLoadingPreview(false);
      }
    };

    decodeTiff();

    return () => {
      setIsLoadingPreview(false);
    };
  }, [previewFile]);

  // Clean up preview URL on unmount or when it changes
  React.useEffect(() => {
    return () => {
      if (previewUrl && previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  // Scroll to result hook
  const { registerResultRef, scrollToResult } = useScrollToResult();

  // Auth
  const { userEmail, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();

  const handleLogout = async () => {
    await logout();
    toast.success('Logged out successfully');
  };

  // ============ File Management ============

  const validateFile = (file: File): boolean => {
    const extension = '.' + file.name.split('.').pop()?.toLowerCase();
    return ALLOWED_FORMATS.includes(extension);
  };

  const handleFiles = (files: FileList | null) => {
    if (!files) return;

    const validFiles: UploadedFile[] = [];
    const invalidFiles: string[] = [];

    Array.from(files).forEach((file) => {
      if (validateFile(file)) {
        validFiles.push({
          file,
          id: Math.random().toString(36).slice(2, 11),
          status: 'pending',
        });
      } else {
        invalidFiles.push(file.name);
      }
    });

    if (invalidFiles.length > 0) {
      toast.error(`Invalid file format(s)`, {
        description: `${invalidFiles.join(', ')} - Allowed: ${ALLOWED_FORMATS.join(', ')}`,
      });
    }

    if (validFiles.length > 0) {
      setUploadedFiles((prev) => [...prev, ...validFiles]);
      toast.success(`${validFiles.length} file(s) added`);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFiles(e.target.files);
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const removeFile = (id: string) => {
    setUploadedFiles((prev) => prev.filter((f) => f.id !== id));
  };

  const clearAllFiles = () => {
    setUploadedFiles([]);
    setBatchHistograms({});
    setExclusionsByFile({});
    setRegeneratedHistogramsByFile({});
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  // ============ Image Processing ============

  const processImage = async (uploadedFile: UploadedFile, signal?: AbortSignal): Promise<InferenceResult | null> => {
    const formData = new FormData();
    formData.append('file', uploadedFile.file);
    const requestUrl = `${API_BASE_URL}/inference/detect?conf_threshold=${confThreshold}&return_image=true&pixel_to_um=${pixelToUm}`;

    try {
      console.info('[API][inference/detect] request', {
        url: requestUrl,
        filename: uploadedFile.file.name,
        bytes: uploadedFile.file.size,
      });

      const response = await fetch(requestUrl, {
        method: 'POST',
        body: formData,
        credentials: 'include',
        signal,
      });

      return await parseApiResponse<InferenceResult>(response, 'inference/detect');
    } catch (error) {
      console.error('[API][inference/detect] exception', {
        filename: uploadedFile.file.name,
        error,
      });
      throw error;
    }
  };

  const fetchBatchHistogramsForFiles = async (
    files: UploadedFile[],
    exclusions?: Record<string, Set<number>>,
    sizeFilter?: { enabled: boolean; range: [number, number] }
  ) => {
    const doneFiles = files.filter((f) => f.status === 'done' && f.result);
    if (doneFiles.length < 1) {
      setBatchHistograms({});
      return;
    }

    setIsLoadingHistograms(true);
    const newHistograms: Record<string, BatchHistogram> = {};

    const allSizesByClass: Record<string, number[]> = {};
    doneFiles.forEach((file) => {
      const fileExclusions = exclusions?.[file.id] || new Set<number>();
      file.result?.results.detections.forEach((det, index) => {
        if (fileExclusions.has(index)) return;
        if (sizeFilter?.enabled && det.size?.diameter_um) {
          if (det.size.diameter_um < sizeFilter.range[0] || det.size.diameter_um > sizeFilter.range[1]) {
            return;
          }
        }
        if (det.size?.diameter_um) {
          if (!allSizesByClass[det.class_name]) {
            allSizesByClass[det.class_name] = [];
          }
          allSizesByClass[det.class_name].push(det.size.diameter_um);
        }
      });
    });

    for (const [className, sizes] of Object.entries(allSizesByClass)) {
      if (sizes.length === 0) continue;
      try {
        const response = await fetch(`${API_BASE_URL}/inference/batch-histogram`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            sizes,
            class_name: className,
            title: `${className.replace(/_/g, ' ')} - Size Distribution (n=${sizes.length})`,
          }),
        });
        const data = await parseApiResponse<BatchHistogram>(response, `inference/batch-histogram:${className}`);
        newHistograms[className] = data;
      } catch (error) {
        console.error(`Failed to fetch histogram for ${className}:`, error);
      }
    }

    setBatchHistograms(newHistograms);
    setIsLoadingHistograms(false);
  };

  const handleProcessImages = async () => {
    const pendingFiles = uploadedFiles.filter((f) => f.status === 'pending');
    if (pendingFiles.length === 0) return;

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setIsProcessing(true);
    setProcessingProgress(0);
    setBatchHistograms({});
    setPreviewFileId(undefined); // Clear preview when processing starts
    let processed = 0;
    let successCount = 0;
    let errorCount = 0;
    let wasCancelled = false;
    let updatedFiles = [...uploadedFiles];

    for (const file of pendingFiles) {
      if (abortController.signal.aborted) {
        wasCancelled = true;
        break;
      }

      setUploadedFiles((prev) =>
        prev.map((f) => (f.id === file.id ? { ...f, status: 'processing' as const } : f))
      );

      try {
        const result = await processImage(file, abortController.signal);
        const updatedFile = { ...file, status: 'done' as const, result: result || undefined };
        updatedFiles = updatedFiles.map((f) => (f.id === file.id ? updatedFile : f));
        setUploadedFiles((prev) =>
          prev.map((f) => (f.id === file.id ? updatedFile : f))
        );
        successCount++;
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
          const updatedFile = { ...file, status: 'pending' as const };
          updatedFiles = updatedFiles.map((f) => (f.id === file.id ? updatedFile : f));
          setUploadedFiles((prev) =>
            prev.map((f) => (f.id === file.id ? updatedFile : f))
          );
          wasCancelled = true;
          break;
        }
        const updatedFile = { ...file, status: 'error' as const, error: error instanceof Error ? error.message : 'Unknown error' };
        updatedFiles = updatedFiles.map((f) => (f.id === file.id ? updatedFile : f));
        setUploadedFiles((prev) =>
          prev.map((f) => (f.id === file.id ? updatedFile : f))
        );
        errorCount++;
      }

      processed++;
      setProcessingProgress((processed / pendingFiles.length) * 100);
    }

    abortControllerRef.current = null;
    setIsProcessing(false);

    if (wasCancelled) {
      const remaining = pendingFiles.length - processed;
      toast.info('Processing cancelled', {
        description: `${successCount} completed, ${remaining} remaining. You can resume anytime.`,
      });
    } else if (successCount > 0 && errorCount === 0) {
      toast.success(`Successfully processed ${successCount} image(s)`);
    } else if (errorCount > 0) {
      toast.error(`Processed with errors`, {
        description: `${successCount} succeeded, ${errorCount} failed`,
      });
    }

    const totalDone = updatedFiles.filter((f) => f.status === 'done' && f.result).length;
    if (totalDone >= 1) {
      fetchBatchHistogramsForFiles(updatedFiles);
    }
  };

  const handleCancelProcessing = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      toast.info('Cancelling...', { description: 'Stopping after current image completes' });
    }
  };

  // ============ Computed Values ============

  const pendingCount = uploadedFiles.filter((f) => f.status === 'pending').length;
  const processedFiles = uploadedFiles.filter((f) => f.status === 'done' && f.result);

  // Global size bounds
  const globalSizeBounds = React.useMemo(() => {
    let minSize = Infinity;
    let maxSize = -Infinity;

    processedFiles.forEach((file) => {
      const result = file.result;
      if (!result) return;
      const fileExclusions = exclusionsByFile[file.id] || new Set<number>();
      result.results.detections.forEach((det, index) => {
        if (fileExclusions.has(index)) return;
        if (det.size?.diameter_um) {
          minSize = Math.min(minSize, det.size.diameter_um);
          maxSize = Math.max(maxSize, det.size.diameter_um);
        }
      });
    });

    if (minSize === Infinity || maxSize === -Infinity) {
      return { min: 0, max: 100 };
    }
    return {
      min: Math.floor(minSize * 10) / 10,
      max: Math.ceil(maxSize * 10) / 10,
    };
  }, [processedFiles, exclusionsByFile]);

  // Reset size filter when data changes
  React.useEffect(() => {
    if (globalSizeBounds.min !== 0 || globalSizeBounds.max !== 100) {
      setSizeFilterRange([globalSizeBounds.min, globalSizeBounds.max]);
      setAppliedSizeFilterRange([globalSizeBounds.min, globalSizeBounds.max]);
    }
  }, [globalSizeBounds.min, globalSizeBounds.max]);

  const isSizeFilterActive = sizeFilterEnabled && (
    sizeFilterRange[0] > globalSizeBounds.min ||
    sizeFilterRange[1] < globalSizeBounds.max
  );

  // Compute batch analysis
  const computeBatchAnalysis = React.useMemo((): BatchAnalysisData | null => {
    if (processedFiles.length === 0) return null;

    const passesFilter = (det: Detection): boolean => {
      if (!isSizeFilterActive) return true;
      if (!det.size?.diameter_um) return true;
      return det.size.diameter_um >= sizeFilterRange[0] && det.size.diameter_um <= sizeFilterRange[1];
    };

    const aggregatedCounts: Record<string, number> = {};
    const allSizesByClass: Record<string, number[]> = {};
    const shapeMetricsByClass: Record<string, { roundness: number[]; convexity: number[]; aspect_ratio: number[]; solidity: number[] }> = {};
    let totalActiveDetections = 0;

    processedFiles.forEach((file) => {
      const result = file.result;
      if (!result) return;
      const fileExclusions = exclusionsByFile[file.id] || new Set<number>();

      result.results.detections.forEach((det, index) => {
        if (fileExclusions.has(index)) return;
        if (!passesFilter(det)) return;
        totalActiveDetections++;
        aggregatedCounts[det.class_name] = (aggregatedCounts[det.class_name] || 0) + 1;
        if (det.size?.diameter_um) {
          if (!allSizesByClass[det.class_name]) {
            allSizesByClass[det.class_name] = [];
          }
          allSizesByClass[det.class_name].push(det.size.diameter_um);
        }
      });

      const statsByClass = result.results.statistics?.by_class;
      if (statsByClass) {
        const activeCountsThisFile: Record<string, number> = {};
        result.results.detections.forEach((det, index) => {
          if (fileExclusions.has(index)) return;
          if (!passesFilter(det)) return;
          activeCountsThisFile[det.class_name] = (activeCountsThisFile[det.class_name] || 0) + 1;
        });

        Object.entries(statsByClass).forEach(([className, stats]: [string, any]) => {
          const activeCount = activeCountsThisFile[className] || 0;
          if (activeCount > 0 && stats.shape_metrics) {
            if (!shapeMetricsByClass[className]) {
              shapeMetricsByClass[className] = { roundness: [], convexity: [], aspect_ratio: [], solidity: [] };
            }
            for (let i = 0; i < activeCount; i++) {
              shapeMetricsByClass[className].roundness.push(stats.shape_metrics.roundness);
              shapeMetricsByClass[className].convexity.push(stats.shape_metrics.convexity);
              shapeMetricsByClass[className].aspect_ratio.push(stats.shape_metrics.aspect_ratio);
              shapeMetricsByClass[className].solidity.push(stats.shape_metrics.solidity);
            }
          }
        });
      }
    });

    const batchStatsByClass: BatchAnalysisData['statsByClass'] = {};

    Object.keys(aggregatedCounts).forEach((className) => {
      const sizes = allSizesByClass[className] || [];
      const shapeData = shapeMetricsByClass[className];

      let sizeStats = {
        mean_um: null as number | null,
        median_um: null as number | null,
        std_um: null as number | null,
        min_um: null as number | null,
        max_um: null as number | null,
      };

      if (sizes.length > 0) {
        const sorted = [...sizes].sort((a, b) => a - b);
        const mean = sizes.reduce((a, b) => a + b, 0) / sizes.length;
        const median = sorted.length % 2 === 0
          ? (sorted[sorted.length / 2 - 1] + sorted[sorted.length / 2]) / 2
          : sorted[Math.floor(sorted.length / 2)];
        const variance = sizes.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / sizes.length;
        const std = Math.sqrt(variance);

        sizeStats = {
          mean_um: mean,
          median_um: median,
          std_um: std,
          min_um: sorted[0],
          max_um: sorted[sorted.length - 1],
        };
      }

      let shapeMetrics = null;
      if (shapeData && shapeData.roundness.length > 0) {
        const avg = (arr: number[]) => arr.reduce((a, b) => a + b, 0) / arr.length;
        shapeMetrics = {
          roundness: avg(shapeData.roundness),
          convexity: avg(shapeData.convexity),
          aspect_ratio: avg(shapeData.aspect_ratio),
          solidity: avg(shapeData.solidity),
        };
      }

      batchStatsByClass[className] = {
        count: aggregatedCounts[className],
        ...sizeStats,
        shape_metrics: shapeMetrics,
      };
    });

    return {
      totalImages: processedFiles.length,
      totalDetections: totalActiveDetections,
      classCounts: aggregatedCounts,
      statsByClass: batchStatsByClass,
    };
  }, [processedFiles, exclusionsByFile, isSizeFilterActive, sizeFilterRange]);

  // Size filtered count
  const sizeFilteredCount = React.useMemo(() => {
    if (!isSizeFilterActive) return 0;
    let filteredOut = 0;
    processedFiles.forEach((file) => {
      const result = file.result;
      if (!result) return;
      const fileExclusions = exclusionsByFile[file.id] || new Set<number>();
      result.results.detections.forEach((det, index) => {
        if (fileExclusions.has(index)) return;
        if (det.size?.diameter_um) {
          if (det.size.diameter_um < sizeFilterRange[0] || det.size.diameter_um > sizeFilterRange[1]) {
            filteredOut++;
          }
        }
      });
    });
    return filteredOut;
  }, [processedFiles, exclusionsByFile, sizeFilterRange, isSizeFilterActive]);

  // Debounced histogram regeneration
  const sizeFilterTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pendingSizeFilterUpdate = sizeFilterRange[0] !== appliedSizeFilterRange[0] || sizeFilterRange[1] !== appliedSizeFilterRange[1];

  React.useEffect(() => {
    if (processedFiles.length === 0 || !sizeFilterEnabled) return;
    if (sizeFilterTimeoutRef.current) {
      clearTimeout(sizeFilterTimeoutRef.current);
    }
    sizeFilterTimeoutRef.current = setTimeout(() => {
      if (sizeFilterRange[0] !== appliedSizeFilterRange[0] || sizeFilterRange[1] !== appliedSizeFilterRange[1]) {
        setAppliedSizeFilterRange(sizeFilterRange);
        const newFilterActive = sizeFilterRange[0] > globalSizeBounds.min || sizeFilterRange[1] < globalSizeBounds.max;
        toast.info('Regenerating histograms...', { duration: 2000 });
        fetchBatchHistogramsForFiles(
          uploadedFiles,
          exclusionsByFile,
          newFilterActive ? { enabled: true, range: sizeFilterRange } : undefined
        );
      }
    }, 5000);
    return () => {
      if (sizeFilterTimeoutRef.current) {
        clearTimeout(sizeFilterTimeoutRef.current);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sizeFilterRange[0], sizeFilterRange[1], sizeFilterEnabled]);

  // ============ Exclusion Handlers ============

  const handleApplyExclusions = (fileId: string, excludedIndices: Set<number>) => {
    const newExclusions = { ...exclusionsByFile, [fileId]: excludedIndices };
    setExclusionsByFile(newExclusions);
    toast.success(`Applied ${excludedIndices.size} exclusion(s)`, {
      description: 'Regenerating histograms...'
    });
    fetchBatchHistogramsForFiles(
      uploadedFiles,
      newExclusions,
      isSizeFilterActive ? { enabled: true, range: sizeFilterRange } : undefined
    );
  };

  const handleResetExclusions = (fileId: string) => {
    const newExclusions = { ...exclusionsByFile };
    delete newExclusions[fileId];
    setExclusionsByFile(newExclusions);
    setRegeneratedHistogramsByFile(prev => {
      const updated = { ...prev };
      delete updated[fileId];
      return updated;
    });
    toast.info('Reset exclusions', {
      description: 'Regenerating histograms...'
    });
    fetchBatchHistogramsForFiles(
      uploadedFiles,
      newExclusions,
      isSizeFilterActive ? { enabled: true, range: sizeFilterRange } : undefined
    );
  };

  // ============ File Click Handler ============

  const handleFileClick = (fileId: string) => {
    const file = uploadedFiles.find(f => f.id === fileId);

    if (!file) return;

    // For processed files: scroll to result and clear preview
    if (file.status === 'done' && file.result) {
      setActiveFileId(fileId);
      setPreviewFileId(undefined); // Clear preview when viewing results
      scrollToResult(fileId);
    }
    // For pending files: toggle preview
    else if (file.status === 'pending') {
      if (previewFileId === fileId) {
        // Clicking the same file again closes preview
        setPreviewFileId(undefined);
        setActiveFileId(undefined);
      } else {
        // Show preview for this file
        setPreviewFileId(fileId);
        setActiveFileId(fileId);
      }
    }
  };

  // ============ Size Filter Handlers ============

  const handleSizeFilterReset = () => {
    setSizeFilterRange([globalSizeBounds.min, globalSizeBounds.max]);
  };

  const handleSizeFilterApplyNow = () => {
    if (sizeFilterTimeoutRef.current) {
      clearTimeout(sizeFilterTimeoutRef.current);
    }
    setAppliedSizeFilterRange(sizeFilterRange);
    toast.info('Regenerating histograms...', { duration: 2000 });
    fetchBatchHistogramsForFiles(
      uploadedFiles,
      exclusionsByFile,
      isSizeFilterActive ? { enabled: true, range: sizeFilterRange } : undefined
    );
  };

  // ============ Render ============

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-gray-900 dark:to-gray-950">
      {/* Header */}
      <header className="bg-white dark:bg-gray-900 border-b dark:border-gray-800 shadow-sm sticky top-0 z-40">
        <div className="px-4 sm:px-6 py-4 flex justify-between items-center">
          {/* Mobile menu button */}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileDrawerOpen(true)}
            className="md:hidden"
          >
            <Menu className="w-5 h-5" />
          </Button>

          {/* Logo */}
          <div className="flex flex-col items-start">
            <img
              src={theme === 'light' ? '/assets/persist-logo-black.avif' : '/assets/persist-logo-white.png'}
              alt="Persist AI"
              className="h-8 w-auto"
            />
            <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mt-1">AI Microparticle Analysis</h1>
          </div>

          {/* User info */}
          <div className="flex items-center gap-4">
            {/* Theme toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className="h-9 w-9"
            >
              {theme === 'light' ? (
                <Moon className="w-5 h-5" />
              ) : (
                <Sun className="w-5 h-5" />
              )}
            </Button>

            <div className="text-right hidden sm:block">
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{userEmail || 'User'}</p>
              <Badge variant="secondary" className="text-xs">Authenticated</Badge>
            </div>
            <Button variant="outline" size="sm" onClick={handleLogout} className="gap-2">
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Desktop Sidebar */}
      <AppSidebar
        isDragging={isDragging}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onBrowseClick={handleBrowseClick}
        fileInputRef={fileInputRef}
        onFileInputChange={handleFileInputChange}
        allowedFormats={ALLOWED_FORMATS}
        confThreshold={confThreshold}
        onConfThresholdChange={setConfThreshold}
        pixelToUm={pixelToUm}
        onPixelToUmChange={setPixelToUm}
        files={uploadedFiles}
        onRemoveFile={removeFile}
        onClearAll={clearAllFiles}
        onFileClick={handleFileClick}
        activeFileId={activeFileId}
        formatFileSize={formatFileSize}
        isProcessing={isProcessing}
        processingProgress={processingProgress}
        pendingCount={pendingCount}
        hasResults={processedFiles.length > 0}
        onProcess={handleProcessImages}
        onCancel={handleCancelProcessing}
        onExport={() => setIsExportModalOpen(true)}
      />

      {/* Mobile Drawer */}
      <MobileDrawer
        isOpen={mobileDrawerOpen}
        onClose={() => setMobileDrawerOpen(false)}
        isDragging={isDragging}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onBrowseClick={handleBrowseClick}
        fileInputRef={fileInputRef}
        onFileInputChange={handleFileInputChange}
        allowedFormats={ALLOWED_FORMATS}
        confThreshold={confThreshold}
        onConfThresholdChange={setConfThreshold}
        pixelToUm={pixelToUm}
        onPixelToUmChange={setPixelToUm}
        files={uploadedFiles}
        onRemoveFile={removeFile}
        onClearAll={clearAllFiles}
        onFileClick={handleFileClick}
        activeFileId={activeFileId}
        formatFileSize={formatFileSize}
        isProcessing={isProcessing}
        processingProgress={processingProgress}
        pendingCount={pendingCount}
        hasResults={processedFiles.length > 0}
        onProcess={handleProcessImages}
        onCancel={handleCancelProcessing}
        onExport={() => setIsExportModalOpen(true)}
      />

      {/* Main Content */}
      <main className={`min-h-screen pt-6 pb-8 px-4 sm:px-6 lg:px-8 ${SIDEBAR_CLASSES.marginLeft}`}>
        <div className="max-w-5xl mx-auto space-y-6">
          {/* Empty State / Preview */}
          {processedFiles.length === 0 && (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-16 text-center">
                {/* Show preview if a pending file is selected */}
                {previewFile ? (
                  isLoadingPreview ? (
                    <div className="w-full max-w-2xl">
                      <p className="text-sm font-medium text-foreground mb-3 truncate" title={previewFile.file.name}>
                        {previewFile.file.name}
                      </p>
                      <div className="relative rounded-lg overflow-hidden border-2 border-orange-500 shadow-lg p-16 bg-muted/30">
                        <div className="flex flex-col items-center justify-center gap-4">
                          <Loader2 className="w-16 h-16 text-orange-500 animate-spin" />
                          <div className="text-center">
                            <p className="text-sm font-medium text-foreground">Loading preview...</p>
                            <p className="text-xs text-muted-foreground mt-1">
                              Converting TIFF image
                            </p>
                          </div>
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground mt-3">
                        {uploadedFiles.length} file(s) ready. Click "Process" to start analysis.
                      </p>
                    </div>
                  ) : previewUrl ? (
                    <div className="w-full max-w-2xl">
                      <p className="text-sm font-medium text-foreground mb-3 truncate" title={previewFile.file.name}>
                        {previewFile.file.name}
                      </p>
                      <div className="relative rounded-lg overflow-hidden border-2 border-orange-500 shadow-lg">
                        <img
                          src={previewUrl}
                          alt={previewFile.file.name}
                          className="w-full h-auto max-h-[60vh] object-contain bg-white dark:bg-gray-900"
                        />
                      </div>
                      <p className="text-xs text-muted-foreground mt-3">
                        {uploadedFiles.length} file(s) ready. Click "Process" to start analysis.
                      </p>
                    </div>
                  ) : (
                    <div className="w-full max-w-2xl">
                      <p className="text-sm font-medium text-foreground mb-3 truncate" title={previewFile.file.name}>
                        {previewFile.file.name}
                      </p>
                      <div className="relative rounded-lg overflow-hidden border-2 border-orange-500 shadow-lg p-16 bg-muted/30">
                        <div className="flex flex-col items-center justify-center gap-4">
                          <FileImage className="w-16 h-16 text-muted-foreground" />
                          <div className="text-center">
                            <p className="text-sm font-medium text-foreground">Preview failed</p>
                            <p className="text-xs text-muted-foreground mt-1">
                              Unable to load image preview
                            </p>
                            <p className="text-xs text-muted-foreground">
                              Click "Process" to analyze this image
                            </p>
                          </div>
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground mt-3">
                        {uploadedFiles.length} file(s) ready. Click "Process" to start analysis.
                      </p>
                    </div>
                  )
                ) : (
                  <>
                    <div className="w-20 h-20 bg-muted rounded-full flex items-center justify-center mb-4">
                      <BarChart3 className="w-10 h-10 text-muted-foreground" />
                    </div>
                    <CardTitle className="text-xl mb-2">No Results Yet</CardTitle>
                    <CardDescription className="max-w-md">
                      {uploadedFiles.length === 0
                        ? 'Upload images using the sidebar to begin detection and analysis.'
                        : `${uploadedFiles.length} file(s) ready. Click on a file to preview or click "Process" to start analysis.`
                      }
                    </CardDescription>
                    {uploadedFiles.length === 0 && (
                      <Button
                        variant="outline"
                        className="mt-4 md:hidden"
                        onClick={() => setMobileDrawerOpen(true)}
                      >
                        Open Upload Panel
                      </Button>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {/* Batch Analysis */}
          {computeBatchAnalysis && processedFiles.length >= 1 && (
            <BatchAnalysis
              batchAnalysis={computeBatchAnalysis}
              batchHistograms={batchHistograms}
              isLoadingHistograms={isLoadingHistograms}
              onExport={() => setIsExportModalOpen(true)}
              sizeFilterComponent={
                globalSizeBounds.min !== globalSizeBounds.max && (
                  <SizeFilter
                    enabled={sizeFilterEnabled}
                    range={sizeFilterRange}
                    globalBounds={globalSizeBounds}
                    filteredOutCount={sizeFilteredCount}
                    pendingUpdate={pendingSizeFilterUpdate}
                    onRangeChange={setSizeFilterRange}
                    onReset={handleSizeFilterReset}
                    onApplyNow={handleSizeFilterApplyNow}
                  />
                )
              }
            />
          )}

          {/* Individual Results */}
          {processedFiles.length > 0 && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-muted rounded-lg">
                      <BarChart3 className="w-5 h-5 text-muted-foreground" />
                    </div>
                    <div>
                      <CardTitle>Individual Image Results</CardTitle>
                      <CardDescription>
                        {processedFiles.length} image{processedFiles.length !== 1 ? 's' : ''} analyzed
                      </CardDescription>
                    </div>
                  </div>
                  <Badge variant="secondary" className="text-base px-4 py-1.5 font-semibold">
                    {computeBatchAnalysis?.totalDetections ?? 0} Total Detections
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {processedFiles.map((file) => (
                    file.result && (
                      <DetectionResultCard
                        key={file.id}
                        ref={(el) => registerResultRef(file.id, el)}
                        filename={file.file.name}
                        result={file.result}
                        classColors={CLASS_COLORS}
                        classBadgeVariants={CLASS_BADGE_VARIANTS}
                        pixelToUm={pixelToUm}
                        confThreshold={confThreshold}
                        onApplyExclusions={(excludedIndices) => handleApplyExclusions(file.id, excludedIndices)}
                        onResetExclusions={() => handleResetExclusions(file.id)}
                        onHistogramsRegenerated={(histograms) => {
                          setRegeneratedHistogramsByFile(prev => ({
                            ...prev,
                            [file.id]: histograms
                          }));
                        }}
                        sizeFilter={isSizeFilterActive ? { min: sizeFilterRange[0], max: sizeFilterRange[1] } : undefined}
                        sizeFilterApplied={isSizeFilterActive ? { min: appliedSizeFilterRange[0], max: appliedSizeFilterRange[1] } : undefined}
                      />
                    )
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </main>

      {/* Export Modal */}
      {computeBatchAnalysis && (
        <ExportModal
          isOpen={isExportModalOpen}
          onClose={() => setIsExportModalOpen(false)}
          exportData={{
            processedFiles: uploadedFiles.filter(f => f.status === 'done' && f.result) as ExportData['processedFiles'],
            batchAnalysis: computeBatchAnalysis,
            batchHistograms,
            exclusionsByFile,
            regeneratedHistogramsByFile,
            pixelToUm,
            confThreshold,
            sizeFilter: isSizeFilterActive ? { min: appliedSizeFilterRange[0], max: appliedSizeFilterRange[1] } : undefined,
          }}
        />
      )}
    </div>
  );
};

export default LandingPage;
